import React from "react";
import Overlay from "../Overlay";
import SimpleTable from "../tables/SimpleTable";
import FilterCard, { FIELD_TYPE, ReactSelect } from "../FilterCard";
import {
  getBoolSelectVal,
  getNumPages,
  getPageOptions,
  getSelectLabels,
  hideModal,
  showModalNoOutsideClick,
  slicePages,
} from "../../util/FormatUtil";
import { sweetalert } from "../../App";
import Select from "react-select";
import NetworkUtil from "../../network/NetworkUtil";
import { Column } from "../tables/TableBase";
import { Sorter, TableOrder } from "../../sorting/Sorter";
import PaginationTool from "../PaginationTool";
import SystemAPI from "../../network/SystemAPI";
import { AdminPages } from "./AdminNav";
import { SubmittingProvider } from "../../types/SubmittingProvider";
import ProviderAPI from "../../network/ProviderAPI";
import { ProviderManagementModal } from "../modals/ProviderManagementModal";
import AdminAPI from "../../network/AdminAPI";

// import { SpecimenSource } from "../../types/SpecimenSource";
// import SpecimenSourceAPI from "../../network/SpecimenSourceAPI";
// import { SpecimenSourceManagementModal } from "../modals/SpecimenSourceManagementModal";

const ITEMS_PER_PAGE = 25;

interface ProviderManagementState {
  showLoading: boolean;
  selectedProvider: SubmittingProvider;
  providerQueryResults: any[];
  tableData: {}[];
  filter?: {
    FullName;
    NPI_Number;
    ContactID;
    FacilityID;
  };
  direction?: TableOrder;
  sortKey?: string;
  sortDirection?: TableOrder;
  page_options: ReactSelect[];
  selected_page?: { label; value };
  providerList: { label; value }[];
  NPIList: { label; value }[];
  contactIDList: { label; value }[];
  facilityList: { label; value }[];
  pageCustomQuestions?
  states
  facilities: any[];
  facilityLabels: { label; value }[];
}

export class ProviderManagement extends React.Component<
  any,
  ProviderManagementState
> {
  
  constructor(props) {
    super(props);

    this.state = {
      showLoading: false,
      selectedProvider: {} as SubmittingProvider,
      tableData: [] as any,
      providerQueryResults: [] as any,
      filter: {
        FullName: [],
        NPI_Number: [],
        ContactID: [],
        FacilityID: [],
      },
      direction: "asc",
      page_options: [{ label: "1", value: "1" }],
      selected_page: { label: "1", value: 1 },
      providerList: [],
      NPIList: [],
      contactIDList: [],
      facilityList:[],
      pageCustomQuestions: [],
      states: [],
      facilities: [],
      facilityLabels: []
    };

    this.createProvider = this.createProvider.bind(this);
  }

  componentDidMount() {
    document.title = 'Providers Management Page';
    this.loadProviderData();

    // SystemAPI.getCustomQuestions(AdminPages.Providers).then(res =>{
    //   this.setState({pageCustomQuestions:res.questions})
    // })

    SystemAPI.getAllStates().then(states => {
      this.setState({ states: states });
    })

    AdminAPI.getFacilitiesForSubmissionForm().then(data => {
        this.setState({ 
          facilities: data.authorizedFacilities, 
          facilityLabels: getSelectLabels(data.authorizedFacilities) 
        })
    })
  }

  /**
   * Returns a unique array of objects from arr based on labelKey and valueKey, sorted by label.
   */
  async getUniqueByKeySorted<T extends Record<string, any>>(
    arr: T[], // array of objects to be filtered and sorted
    labelKey: keyof T, // label for the returned object
    valueKey: keyof T // value for the returned object
  ): Promise<Array<{ label: string; value: string | number }>> {

    const daUniqueArray: Array<{ label: string; value: string | number }> = [];
    const seen = new Set<string | number>();

    arr.forEach(obj => {
      if (
        obj &&
        typeof obj === 'object' &&
        obj[labelKey] !== undefined &&
        obj[valueKey] !== undefined &&
        obj[labelKey] !== null &&
        obj[valueKey] !== null
      ) {
        const label = obj[labelKey];
        const value = obj[valueKey];
        if (!seen.has(label)) {
          seen.add(label);
          daUniqueArray.push({ label: String(label), value: value as string | number });
        }
      }
    });
    return daUniqueArray.sort((a, b) => a.label.localeCompare(b.label));
  }

  loadProviderData() {
    this.setState({ showLoading: true }, async () => {

      try {
        let providerQuery = await ProviderAPI.getAllSubmittingProviders();
        if (!providerQuery || providerQuery.success === false) {
          this.setState({ showLoading: false, tableData: [], providerQueryResults: null });
          return sweetalert.fire({
            icon: "info",
            title: "",
            text: `Unable to retrieve Provider data at this time. ${providerQuery?.reason || ""}`,
          });
        }
        let providerData = providerQuery.data ? providerQuery.data : [];

        this.setState({
          providerList: await this.getUniqueByKeySorted(providerData, 'FullName', 'FullName'),  // used to populate the Provider filter dropdown
          facilityList: await this.getUniqueByKeySorted(providerData, 'FacilityName', 'FacilityID'), // used to populate the Facility filter dropdown
          NPIList: await this.getUniqueByKeySorted(providerData, 'NPI_Number', 'NPI_Number'),  // used to populate the NPI filter dropdown
          contactIDList: await this.getUniqueByKeySorted(providerData, 'ContactID', 'ContactID'), // used to populate the Contact ID filter dropdown
        });

        // With no filter set, this list is what /filter would return (both use the same
        // facility scope on the backend), so show it instead of making a second request.
        if (this.isFilterEmpty()) {
          this.showResults(providerData, this.state.selected_page.value);
        } else {
          this.queryProviderData(this.state.selected_page.value);
        }
      } catch (e) {
        console.error(e);
        this.setState({ showLoading: false, tableData: [], providerQueryResults: null });
        return sweetalert.fire({
          icon: "error",
          title: "",
          text: "Error retrieving Provider data.",
        });
      }
    });
  }

  // showNoResults: tell the user when a search matches nothing (not on the initial load)
  queryProviderData(page: number, showNoResults = false) {
    this.setState({ showLoading: true }, async () => {
      let filterFromState = {
          FullName: this.state.filter.FullName,
          NPI_Number: this.state.filter.NPI_Number,
          ContactID: this.state.filter.ContactID,
          FacilityID: this.state.filter.FacilityID,
      }

      let providers;
      try {
        providers = await ProviderAPI.filterProviders({
          filter: filterFromState
        });
      } catch (e) {
        console.error(e);
        providers = { success: false, reason: "" };
      }

      if (!providers || providers.success === false) {
        this.setState({ showLoading: false, tableData: [], providerQueryResults: null });
        return sweetalert.fire({
          title: "",
          text: `Unable to retrieve Provider data at this time. ${providers?.reason || ""}`,
          icon: "info",
        });
      }

      // data is [rows]
      this.showResults(providers.data && providers.data[0] ? providers.data[0] : [], page, showNoResults);
    });
  }

  isFilterEmpty(): boolean {
    const filter = this.state.filter || {};
    return ["FullName", "NPI_Number", "ContactID", "FacilityID"]
      .every(key => !Array.isArray(filter[key]) || filter[key].length === 0);
  }

  // Puts results in the table and hides the loading overlay
  showResults(results: any[], page: number, showNoResults = false) {
    if (results.length === 0) {
      this.setState({ showLoading: false, tableData: [], providerQueryResults: [] })
      if (showNoResults) {
        sweetalert.fire({ title: '', text: 'No Providers match your search.', icon: 'info' });
      }
      return;
    }

    // Keep the current sort when searching again
    if (this.state.sortKey) {
      results = new Sorter<any>().sortByKey(results, this.state.sortKey as keyof any, this.state.sortDirection);
    }

    this.setState({
      tableData: slicePages(results, page, ITEMS_PER_PAGE),
      providerQueryResults: results,
      page_options: getPageOptions(getNumPages(results, ITEMS_PER_PAGE)),
      showLoading: false,
    });
  }

  createProvider(provider: SubmittingProvider) {
    this.setState((prevState)=> ({
      showLoading: true,
      selectedProvider: {
        ...prevState.selectedProvider,
        ID: provider.ID ? provider.ID : null, // ID will be null if creating a new provider
        ContactID: provider.ID && provider.ContactID ? provider.ContactID : null,                      // ContactID will be set after provider is created
        FacilityID: this.props.facility?.ID ? this.props.facility.ID : provider.FacilityID ? provider.FacilityID : "",
        FullName: provider.FullName ? provider.FullName.toUpperCase().trim() : "",
        Address: provider.Address ? provider.Address : "",
        Address_A: provider.Address_A ? provider.Address_A : "",
        City: provider.City ? provider.City : "",
        State: provider.State ? provider.State : "",
        Zip: provider.Zip ? provider.Zip : "",
        Phone: provider.Phone ? provider.Phone : "",
        NPI_Number: provider.NPI_Number ? provider.NPI_Number : "",
        CreatedBy: provider.CreatedBy ? provider.CreatedBy : "",
        CreatedDate: provider.CreatedDate ? provider.CreatedDate : new Date(),
        UpdatedBy: provider.UpdatedBy ? provider.UpdatedBy : "",
        UpdatedDate: provider.UpdatedDate ? provider.UpdatedDate : new Date(),
      }
    }), () => {
      // Only new providers come through here - existing ones open read-only
      try{
        ProviderAPI.createProvider(this.state.selectedProvider).then((response) => {
          if (!response.success) {
            this.setState({ showLoading: false });
            return sweetalert.fire({ icon: 'error', title: '', text: response.reason })
          }
          sweetalert.fire({
            icon: 'success',
            title: '',
            text: response.reason
          }).then(() => {
            this.setState({ showLoading: false });
            this.loadProviderData();
            hideModal(ProviderManagementModal.ID);
          })
        });
      } catch (e) {
        console.error("Error creating provider: ", e);
        sweetalert.fire({
          icon: "error",
          title: "",
          text: "An error occurred. Please try again later.",
        });
        this.setState({ showLoading: false });
      }
    });
  }

  handleExportToCSV() {
    this.setState({ showLoading: true }, async () => {
      let succeeded = await NetworkUtil.downloadCSV(
        "/api/admin/providers/csv",
        "Provider Management Data.xlsx",
        { filter: this.state.filter });
      this.setState({ showLoading: false }, () => {
        if (!succeeded) {
          sweetalert.fire({ icon: "error", title: "", text: "Unable to export Provider data at this time." });
        }
      });
    });
  }

  changePage(page: number) {
    let allServices = this.state.providerQueryResults;
    let returnData = slicePages(allServices, page, ITEMS_PER_PAGE);
    this.setState({ tableData: returnData });
  }

  renderProviderFilterFields() {
    return (
      <FilterCard
        fields={[
          {
            label: "Provider",
            key: "FullName",
            type: FIELD_TYPE.SELECT,
            options: this.state.providerList,
            isMapped: true,
            isMulti: true,
            textType: "text",
            isFilter: true,
            popoverText: "Name of Provider"
          },
          {
            label: "Contact ID",
            key: "ContactID",
            type: FIELD_TYPE.SELECT,
            options: this.state.contactIDList,
            isMapped: true,
            isMulti: true,
            textType: "text",
            isFilter: true,
            popoverText: "Contact ID of Provider"
          },
          {
            label: "Facility",
            key: "FacilityID",
            type: FIELD_TYPE.SELECT,
            options: this.state.facilityList,
            isMapped: true,
            isMulti: true,
            textType: "text",
            isFilter: true,
            popoverText: "Facility of Provider"
          },
          {
            label: "NPI Number",
            key: "NPI_Number",
            type: FIELD_TYPE.SELECT,
            options: this.state.NPIList,
            isMapped: true,
            isMulti: true,
            textType: "text",
            isFilter: true,
            popoverText: "NPI Number of Provider"
          },
        ]}
        filterChanged={(e) => this.setState({ filter: e })}
      />
    );
  }

  useSorter(col: Column) {
    // Sort every result, not just the visible page, so paging keeps the order
    let sorted = new Sorter<any>().sortByKey(
      this.state.providerQueryResults || [],
      col.key as keyof any,
      this.state.direction
    );

    this.setState({
      providerQueryResults: sorted,
      tableData: slicePages(sorted, this.state.selected_page.value, ITEMS_PER_PAGE),
      sortKey: col.key as string,
      sortDirection: this.state.direction,
      direction: this.state.direction === "asc" ? "desc" : "asc",
    });
  }

  render() {
    // console.log('Provider Mgmt STATE', this.state);
    return (
      <React.Fragment>
        <PaginationTool />
        <Overlay show_loading={this.state.showLoading} />

        <ProviderManagementModal
          // pageCustomQuestions={this.state.pageCustomQuestions}
          states={this.state.states}
          facilities={this.state.facilities}
          facilityLabels={this.state.facilityLabels}
          selectedProvider={this.state.selectedProvider}
          onSubmit={(provider) => { // provider is this.state.selectedProvider
            // console.log(" Provider Management Modal onSubmit ", provider);
            hideModal(ProviderManagementModal.ID);
            this.createProvider(provider);
          }}
        />
        <div className="container-fluid ">
          <div className={"row"}>
            <div className="col-12 col-md-12 col-lg-8 col-xl-5 pt-2">
              <main id="main-content" tabIndex={-1} aria-label="Provider Management">
                <div className="card mb-2">
                  <div className="card-header verlag-bold">
                    <h3>Provider Management</h3>
                  </div>
                  <div className="card-body">
                    {this.renderProviderFilterFields()}
                  </div>
                  <div className="card-footer">
                    <button
                      className={"btn immySubmitButtonOutline"}
                      onClick={() => {
                        this.setState(
                          {
                            selected_page: { label: 1, value: 1 },
                          },
                          () => {
                            this.queryProviderData(
                              this.state.selected_page.value,
                              true
                            );
                          }
                        );
                      }}
                    >
                      Search
                    </button>
                    <button
                      className="btn btn-outline-success float-right"
                      onClick={() =>
                        this.setState(
                          {
                            selectedProvider: {
                              ID: null,
                              FullName: "",
                              ContactID: "",
                              NPI_Number: "",
                              FacilityID: null,
                              Address: "",
                              Address_A: "",
                              City: "",
                              State: "",
                              Zip: "",
                              Phone: "",
                              CreatedBy: "",
                              CreatedDate: null,
                              UpdatedBy: "",
                              UpdatedDate: null,
                            } as SubmittingProvider,
                          },
                          () => {
                            // TODO:
                            showModalNoOutsideClick(ProviderManagementModal.ID);
                          }
                        )
                      }
                    >
                      Create New
                    </button>
                  </div>
                </div>
              </main>
            </div>
            {this.state.tableData && this.state.tableData.length > 0 && (
              <div className="col-12 col-md-12 col-lg-12 pt-2">
                <div className="card mt-2" style={{marginBottom: '4rem'}}>
                  <div className="card-header verlag-bold stickToTop">
                    <h4 className="text-center text-md-left">
                      Provider List 
                      <section className="tableHeaderSection float-md-right d-flex justify-content-around">
                        <h4 className={"float-md-right"} aria-label="Total Records" role="alert">
                          Total: {this.state.providerQueryResults?.length }
                        </h4>
                        <h4 className="d-inline-block float-right align-middle pr-2 ml-5">
                          Page{" "}
                        </h4>
                        <div className=" align-middle float-right pages ">
                          <Select
                            isSearchable={true}
                            placeholder={"1"}
                            noOptionsMessage={() => "No option"}
                            value={this.state.selected_page}
                            aria-label="Table Page Number"
                            onChange={(e: ReactSelect) =>
                              this.setState({ selected_page: e }, () =>
                                this.changePage(e.value)
                              )
                            }
                            className={"state_select"}
                            options={this.state.page_options}
                          />
                        </div>
                      </section>
                      <button
                        className={"d-none d-md-inline btn btn-outline-primary ml-3"}
                        onClick={() => this.handleExportToCSV()}
                      >
                        Export to CSV
                      </button>
                    </h4>
                  </div>
                  <div className="card-body p-0 m-0 table-responsive">
                    <SimpleTable
                      columns={[
                        {
                          label: "Provider",
                          key: "FullName",
                          popoverText: "Click to Sort By Provider Full Name",
                          rawFormat: (val) => {
                            return (
                              <a
                                href={"#"}
                                className={'tableNameLinkColor'}
                                onClick={() => {
                                  // console.log(" open modal plz ")
                                  this.setState({ selectedProvider: val }, () =>
                                    showModalNoOutsideClick(
                                      ProviderManagementModal.ID
                                    )
                                  );
                                }}
                              >
                                {val.FullName}
                              </a>
                            );
                          },
                        },
                        {
                          label: "Contact ID",
                          key: "ContactID",
                          popoverText: "Click to Sort By Contact ID",
                        },
                        {
                          label: "NPI Number",
                          key: "NPI_Number",
                          popoverText: "Click to Sort By NPI Number",
                        },
                        {
                          label: "Facility",
                          key: "FacilityID",
                          popoverText: "Click to Sort By Facilty",
                          rawFormat: (val) => {
                            return <p className="testsString">{val.FacilityID ? val.FacilityName : 'Not Assigned'}</p>;
                          }
                        },
                        // {
                        //   label: "Is Enabled?",
                        //   key: "IsEnabled",
                        //   popoverText: "Click to Sort By Is Enabled",
                        //   rawFormat: (val) => {
                        //     let string = getBoolSelectVal(val.IsEnabled);
                        //     return <p>{string.label}</p>;
                        //   },
                        // }
                      ]}
                      table_data={this.state.tableData}
                      columnClickedCallback={(col) => {
                        this.useSorter(col);
                      }}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </React.Fragment>
    );
  }
}